import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useModal } from '@ebay/nice-modal-react';
import { compose } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader, Dialog, Floating } from 'components';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import userSearch from 'features/views/userSearch';

import ResultsTable from './components/ResultsTable';

// Gef textFields wants string as input not a number
const normalizeNumbers = (user) => ({ ...user, sellerIn: user.sellerIn ? user.sellerIn.toString() : '' });

// Employees of CP/GLI sometimes must add first name to system so if user is company they just put dot inside
// we have to remove it now
const withoutDot = (user) => ({ ...user, firstName: user.firstName === '.' ? '' : user.firstName });

const normalizeUser = compose(normalizeNumbers, withoutDot);

export const UserSearchModalBody = ({ formData, onSelect, type }) => {
	const dispatch = useDispatch();
	const userSearchModal = useModal(MODALS.user_search);

	const users = useSelector(userSearch.selectors.getRoot);

	useEffect(() => {
		dispatch(userSearch.actions.searchUser({ type, formData }));
		// eslint-disable-next-line
	}, []);

	useEffect(
		() => () => {
			dispatch(userSearch.actions.clearSearch());
			dispatch(dialog.actions.close(userSearch.constants.dialogError));
		},
		[dispatch],
	);

	const handleUserSelection = useCallback(
		(event) => {
			event.preventDefault();
			const selectedUser = users[+event.target.dataset.user];
			onSelect(normalizeUser(selectedUser));
			userSearchModal.hide();
			dispatch(userSearch.actions.clearSearch());
		},
		[dispatch, onSelect, users, userSearchModal],
	);

	const handleOnReload = useCallback(() => {
		dispatch(userSearch.actions.searchUser({ type, formData }));
	}, [dispatch, formData, type]);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.userList} onReload={handleOnReload}>
			<Floating>
				<Dialog name={userSearch.constants.dialogError} mui>
					{getLabelMessage('common:tooManyResults')}
				</Dialog>
			</Floating>
			<ResultsTable users={users} onSelect={handleUserSelection} formData={formData} />
		</ComponentLoader>
	);
};

UserSearchModalBody.propTypes = {
	formData: PropTypes.object,
	onSelect: PropTypes.func,
	type: PropTypes.string,
};

export default UserSearchModalBody;
